import {useEffect, useState, useMemo} from "react";
import {Routes, Route, Navigate, useLocation} from "react-router-dom";
import {ThemeProvider} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import routes from "routes";
import {useMaterialUIController, setMiniSidenav, setOpenConfigurator, setLayout} from "context";
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import bntLogo from "assets/images/Bnt_logo.png";
import {useNavigate} from "react-router-dom";
import {toast, ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import PWAInstallPrompt from "components/PWAInstallPrompt";
import OfflineIndicator from "components/OfflineIndicator";
import {registerServiceWorker, checkForUpdates} from "utils/pwaUtils";
import { setupAuthSync } from "utils/authSync"; 
export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const {pathname} = useLocation();
  const navigate = useNavigate();


  useEffect(() => {
    const initSessionStorage = () => {
      const keys = ["jwtToken", "refreshToken", "login_response_auth", "expirationTime","empId","userType","username","Employee_Details","EmployeeName","EmpName","currentDesignation","photoBase64","Designation"];
      keys.forEach((key) => {
        const value = localStorage.getItem(key);
        if (value && !sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, value);
        }
      });
    };
    initSessionStorage();
  }, []);

  useEffect(() => {
    const checkAuth = () => {
      const loginResponse = localStorage.getItem("login_response_auth") || sessionStorage.getItem("login_response_auth");
      const token = localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");
      const expirationTime = localStorage.getItem("expirationTime") || sessionStorage.getItem("expirationTime");
      if (!loginResponse || !token || (expirationTime && Date.now() > parseInt(expirationTime))) {
        setLayout(dispatch, "vr");
        const allowedPublicRoutes = ["/authentication/sign-in", "/authentication/forgot-password"];
        if (!allowedPublicRoutes.includes(pathname)) {
          navigate("/authentication/sign-in");
        }
      } else {
        setLayout(dispatch, "dashboard");
      }
      setIsLoading(false);
    };

    checkAuth();

    const cleanupAuthSync = setupAuthSync();
   
    document.body.setAttribute("dir", direction);
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  
    return () => {
      cleanupAuthSync();
    };
  }, [dispatch, direction, pathname, navigate]);

  useEffect(() => {
    const initPWA = async () => {
      try {
        const registration = await registerServiceWorker();
        if (registration) {
          // Check for updates
          checkForUpdates(registration);

          // Listen for service worker updates
          registration.addEventListener("updatefound", () => {
            const newWorker = registration.installing;
            newWorker.addEventListener("statechange", () => {
              if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
                // New content is available
                toast.info("New version available! Refresh to update.", {
                  position: "top-center",
                  autoClose: 5000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true
                });
              }
            });
          });
        }
      } catch (error) {
        console.error("PWA initialization failed:", error);
      }
    };

    initPWA();
  }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  const getRoutes = allRoutes =>
    allRoutes.map(route => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }
      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }
      return null;
    });

  const configsButton = (
    <MDBox
      display="none"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{cursor: "pointer"}}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  if (isLoading) {
    return null;
  }

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <ToastContainer />
      <CssBaseline />
      <OfflineIndicator />
      <PWAInstallPrompt />
      {layout === "dashboard" && pathname !== "/authentication/sign-in" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? bntLogo : bntLogo}
            brandName=" "
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {configsButton}
        </>
      )}
      <Routes>
        {getRoutes(routes)}
        <Route path="*" element={<Navigate to="/authentication/sign-in" />} />
      </Routes>
    </ThemeProvider>
  );
}
